/* Home (designs/shared-home.md): the one landing page every signed-in person
   shares. Two doors — Intake and Projects — each with a one-line purpose and
   a REAL count (SH3: a count is read or absent, never a 0 or a guess), a
   "New here?" pointer to About Hermes, and who to contact about a problem.

   "Start here next time" on each door is this browser's convenience (SH4):
   localStorage only, guarded, never the record's. */
import { useEffect, useState, type JSX } from "react";
import type { Capability } from "../../../shared/capabilities";
import { useAppState } from "../shell/store";
import { ViewFrame } from "../shell/ViewFrame";
import { readStartView, writeStartView, type StartDoor } from "../shell/state";
import { asJson } from "../intake/api";
import { ProblemContactsBlock } from "./ProblemContacts";

type Count = { state: "pending" } | { state: "failed" } | { state: "ok"; n: number };

const plural = (n: number, one: string, many: string): string => `${n} ${n === 1 ? one : many}`;

function Door({
  id,
  title,
  purpose,
  count,
  start,
  onOpen,
  onStart,
}: {
  id: StartDoor;
  title: string;
  purpose: string;
  count: string | null;
  start: StartDoor | null;
  onOpen: () => void;
  onStart: (door: StartDoor | null) => void;
}): JSX.Element {
  const remembered = start === id;
  return (
    <section className="card confirmed home-door" data-home-door={id}>
      <div className="card-head">
        <h2 className="title">{title}</h2>
        {count !== null && <span className="count">{count}</span>}
      </div>
      <p>{purpose}</p>
      <div className="form-actions">
        <button className="btn" aria-label={`Open ${title}`} onClick={onOpen}>
          Open {title}
        </button>
        <label className="home-start">
          <input
            type="checkbox"
            checked={remembered}
            aria-label={`Start on ${title} next time`}
            onChange={() => onStart(remembered ? null : id)}
          />{" "}
          Start here next time
        </label>
      </div>
    </section>
  );
}

export function HomeView(): JSX.Element {
  const { state, store } = useAppState();
  const intakeOn = store.on("intake");
  const [efforts, setEfforts] = useState<Count>({ state: "pending" });
  const [start, setStart] = useState<StartDoor | null>(() => readStartView());

  useEffect(() => {
    if (!intakeOn) return;
    fetch("/api/intake/capabilities")
      .then((res) => asJson<Capability[]>(res))
      .then((caps) => setEfforts({ state: "ok", n: caps.filter((c) => c.archived !== true).length }))
      .catch(() => setEfforts({ state: "failed" }));
  }, [intakeOn]);

  const remember = (door: StartDoor | null): void => {
    writeStartView(door);
    setStart(door);
  };

  const effortCount =
    efforts.state === "ok"
      ? `${plural(efforts.n, "effort", "efforts")} registered`
      : efforts.state === "failed"
        ? "count unavailable"
        : null;
  const projectCount = state.loadError
    ? "count unavailable"
    : state.loadPending
      ? null
      : plural(state.projects.length, "project", "projects");

  return (
    <ViewFrame
      banner={
        <div className="banner">
          <b>Hermes.</b>
          <span>
            ASI's living record of its projects, and the register of the large efforts teams need.
          </span>
        </div>
      }
    >
      <div className="home-doors">
        {intakeOn && (
          <Door
            id="intake"
            title="Intake"
            purpose="Register an effort, back one another team already filed, or see what every team is asking for."
            count={effortCount}
            start={start}
            onOpen={() => store.selectNav("intake")}
            onStart={remember}
          />
        )}
        <Door
          id="projects"
          title="Projects"
          purpose="The living record of each project: its charter, roles, record state and charge codes."
          count={projectCount}
          start={start}
          onOpen={() => store.selectNav("projects")}
          onStart={remember}
        />
      </div>
      <section className="about-block">
        <h3>New here?</h3>
        <p>About Hermes explains how the app and its record work, and who can do what.</p>
        <button className="btn ghost small" onClick={() => store.selectNav("about-hermes")}>
          Read About Hermes
        </button>
      </section>
      <ProblemContactsBlock />
    </ViewFrame>
  );
}
